<template>
  <lf-card class="flex w-full flex-col! py-6" :has-padding="false">
    <div class="flex justify-between border-b px-6 pb-6">
      <div class="flex w-full items-center justify-between">
        <div class="relative flex items-center space-x-4">
          <lf-switch
            name="group-visibility"
            :disabled="!canChange"
            :model-value="localState[offerTemplateView].visible"
            @toggle-changed="setGroupVisibility"
          />
          <div
            ref="fieldGroupTitle"
            class="h-8 pt-1 text-sm font-semibold text-gray-600 ring-0 focus:outline-hidden"
            :class="{ 'min-w-28': showPlaceholder }"
            name="fieldGroupTitle"
            :contenteditable="canChange"
            spellcheck="false"
            @keyup="updateTextValue"
            @blur="updateFieldGroupTitle"
          >
            {{ localState[offerTemplateView].title }}
          </div>
          <div
            v-if="showPlaceholder"
            class="pointer-events-none absolute top-1 left-14 text-sm text-gray-400"
            data-cy="placeholder"
          >
            {{ $t("OFFER_CUSTOMISATION.SECTION_NAME") }}
          </div>
          <div
            v-if="canChange"
            v-tooltip="$t('OFFER_CUSTOMISATION.EDIT_SECTION_NAME')"
            @click="focuEditField"
          >
            <icon-base :icon="IconEdit" class="cursor-pointer" />
          </div>
          <div v-if="!localState[offerTemplateView].visible">
            <icon-base
              v-tooltip="$t('OFFER_CUSTOMISATION.SECTION_IS_DISABLED')"
              :icon="IconWarning"
              class="cursor-pointer text-yellow-400"
            />
          </div>
        </div>
        <div
          v-if="canChange"
          v-tooltip="$t('OFFER_CUSTOMISATION.DELETE_SECTION')"
          @click="$emit('removeGroup')"
        >
          <icon-base :icon="IconDelete" class="ml-1 cursor-pointer" />
        </div>
      </div>
    </div>
    <div class="grid grid-cols-2 gap-3 px-6 pt-6">
      <offer-field
        v-for="field in localState.fields"
        :offer-field="field"
        :key="field.id"
        :field="field"
        :can-change="canChangeField(field.id)"
        @remove-field="removeField(field.id)"
        @update-field="updateField"
      />
    </div>
    <add-field-button
      :available-fields="availableFields"
      class="flex-1 px-4"
      @add-field="handleAddField"
    />
  </lf-card>
</template>
<script setup lang="ts">
import { type Ref, computed, inject, ref, useTemplateRef } from "vue";

import { OfferTemplateView } from "@/enums/offerTemplates";

import AddFieldButton from "@/components/deals/workflow/OfferStage/WfbOfferFieldsModal/AddFieldButton.vue";
import OfferField from "@/components/deals/workflow/OfferStage/WfbOfferFieldsModal/OfferField.vue";
import IconDelete from "@/components/icons/IconDelete.vue";
import IconEdit from "@/components/icons/IconEdit.vue";
import IconWarning from "@/components/icons/IconWarning.vue";

import type { FieldGroup, OfferTemplateField } from "@/models/offers";

const props = withDefaults(
  defineProps<{
    fieldGroup: FieldGroup;
    availableFields: Record<string, OfferTemplateField>;
    canChange?: boolean;
  }>(),
  { canChange: true }
);

const emit = defineEmits<{
  removeGroup: [];
  updateGroup: [FieldGroup];
}>();

const offerTemplateView = inject<
  Ref<OfferTemplateView.OFFER | OfferTemplateView.CLOSING>
>("offerTemplateView", ref(OfferTemplateView.OFFER));

const fieldGroupRef = useTemplateRef("fieldGroupTitle");

const localState = ref(props.fieldGroup);

const initialTitle = props.fieldGroup[offerTemplateView.value].title;

const textValue = ref(initialTitle ?? "");
const newFields = ref<string[]>([]);

const showPlaceholder = computed(() => !textValue.value.length);

const canChangeField = (id: string | undefined) => {
  return props.canChange || (!!id && newFields.value.includes(id));
};

const focuEditField = () => {
  if (!fieldGroupRef.value) {
    return;
  }
  fieldGroupRef.value.focus();
  const range = document.createRange();
  const selection = window.getSelection();
  range.selectNodeContents(fieldGroupRef.value);
  range.collapse(false);
  selection?.removeAllRanges();
  selection?.addRange(range);
};

const updateFieldGroupTitle = () => {
  localState.value[offerTemplateView.value].title = textValue.value;
  emit("updateGroup", localState.value);
};

const updateTextValue = (event: KeyboardEvent) => {
  const target = event.target as HTMLSpanElement;
  textValue.value = target.textContent ?? "";
};

const handleAddField = (field: OfferTemplateField) => {
  if (field?.id) {
    newFields.value.push(field.id);
  }
  localState.value.fields.push(field);
  emit("updateGroup", localState.value);
};

const removeField = (fieldId: string | undefined) => {
  if (!fieldId) {
    return;
  }
  localState.value = {
    ...localState.value,
    fields: localState.value.fields.filter((field) => field.id !== fieldId),
  };
  emit("updateGroup", localState.value);
};

const setGroupVisibility = (visible: boolean) => {
  localState.value[offerTemplateView.value].visible = visible;
  emit("updateGroup", localState.value);
};

const updateField = (newField: OfferTemplateField) => {
  localState.value = {
    ...localState.value,
    fields: localState.value.fields.map((offerField) =>
      offerField.id === newField.id ? newField : offerField
    ),
  };
  emit("updateGroup", localState.value);
};
</script>
